import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '@/components/common/Breadcrumb';

interface TimeSlot {
  id: string;
  date: string;
  time: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  topics: string[];
  meetingPlatform: string;
  otherPlatform: string;
  selectedSlots: string[];
  additionalInfo: string;
}

const MeetingBooking = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    topics: [],
    meetingPlatform: 'zoom',
    otherPlatform: '',
    selectedSlots: [],
    additionalInfo: '',
  });

  const availableSlots: TimeSlot[] = [
    { id: 'day1-morning', date: 'Tomorrow', time: '09:00 - 09:30' },
    { id: 'day1-mid1', date: 'Tomorrow', time: '10:00 - 10:30' },
    { id: 'day1-mid2', date: 'Tomorrow', time: '14:00 - 14:30' },
    { id: 'day1-afternoon', date: 'Tomorrow', time: '16:00 - 16:30' },
    { id: 'day2-morning', date: 'Day After Tomorrow', time: '09:00 - 09:30' },
    { id: 'day2-mid1', date: 'Day After Tomorrow', time: '11:00 - 11:30' },
    { id: 'day2-mid2', date: 'Day After Tomorrow', time: '14:00 - 14:30' },
    { id: 'day2-afternoon', date: 'Day After Tomorrow', time: '15:30 - 16:00' },
    { id: 'day3-morning', date: '3 Days Later', time: '09:00 - 09:30' },
    { id: 'day3-mid1', date: '3 Days Later', time: '10:30 - 11:00' },
    { id: 'day3-mid2', date: '3 Days Later', time: '13:30 - 14:00' },
    { id: 'day3-afternoon', date: '3 Days Later', time: '16:00 - 16:30' },
  ];

  const platforms = [
    { id: 'zoom', name: 'Zoom', icon: '📹' },
    { id: 'google-meet', name: 'Google Meet', icon: '📞' },
    { id: 'tencent-meeting', name: 'Tencent Meeting (腾讯会议)', icon: '💬' },
    { id: 'whatsapp-video', name: 'WhatsApp Video Call', icon: '📱' },
    { id: 'wechat-video', name: 'WeChat Video (微信视频)', icon: '💬' },
    { id: 'skype', name: 'Skype', icon: '🔵' },
    { id: 'other', name: 'Other', icon: '🔧' },
  ];

  const topicOptions = [
    'Visa Requirements',
    'Hospital Selection',
    'Doctor Recommendations',
    'Booking Assistance',
    'Hotel Recommendations',
    'Transportation',
    'Language Support',
    'Cost Estimates',
    'Medical Records Translation',
    'Other Questions',
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTopicToggle = (topic: string) => {
    setFormData(prev => ({
      ...prev,
      topics: prev.topics.includes(topic)
        ? prev.topics.filter(t => t !== topic)
        : [...prev.topics, topic]
    }));
  };

  const handleSlotToggle = (slotId: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedSlots.includes(slotId);
      if (isSelected) {
        return { ...prev, selectedSlots: prev.selectedSlots.filter(id => id !== slotId) };
      } else if (prev.selectedSlots.length >= 3) {
        return prev;
      }
      return { ...prev, selectedSlots: [...prev.selectedSlots, slotId] };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      setError('Please fill in all required fields.');
      return;
    }

    if (formData.topics.length === 0) {
      setError('Please select at least one topic.');
      return;
    }

    if (formData.selectedSlots.length === 0) {
      setError('Please select at least 1 time slot (up to 3 preferred slots).');
      return;
    }

    if (formData.meetingPlatform === 'other' && !formData.otherPlatform) {
      setError('Please specify your preferred meeting platform.');
      return;
    }

      setLoading(true);
      setError(null);

      try {
        const platform = formData.meetingPlatform === 'other' ? formData.otherPlatform : formData.meetingPlatform;
        const selectedTimes = formData.selectedSlots.map(slotId => {
          const slot = availableSlots.find(s => s.id === slotId);
          return `${slot?.date} at ${slot?.time}`;
        }).join('; ');

        const bookingData = {
          ...formData,
          platform,
          selectedTimes,
        };

        const response = await fetch('/api/submit-meeting-booking', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...bookingData,
            type: 'meeting',
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to submit booking request');
        }

        sessionStorage.setItem('meetingBooking', JSON.stringify(bookingData));
        setSuccess(true);

        setTimeout(() => {
          setSuccess(false);
          navigate('/meeting-confirmation');
        }, 2000);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to submit booking. Please try again.');
      } finally {
        setLoading(false);
      }
    };

  return (
    <div className="container-custom max-w-3xl">
      <Breadcrumb
        items={[
          { label: 'Home', href: '/' },
          { label: 'Book Meeting' }
        ]}
      />

      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          Book Your Online Meeting
        </h1>
        <p className="text-gray-600">
          Schedule a 30-minute video consultation with our experts to get answers to your questions about visa, hospitals, hotels, and more.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success ? (
        <div className="mb-6 p-6 bg-green-50 border border-green-200 rounded-lg text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-xl font-bold text-green-800 mb-2">Booking Submitted!</h3>
          <p className="text-green-700">Redirecting to payment...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-6 md:p-8">
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">1. Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Your full name"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-600">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="your@email.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone/WhatsApp <span className="text-red-600">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="+1 234 567 8900"
                  required
                />
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">2. Topics You Want to Discuss</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {topicOptions.map(topic => (
                <label
                  key={topic}
                  className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    formData.topics.includes(topic)
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-primary-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.topics.includes(topic)}
                    onChange={() => handleTopicToggle(topic)}
                    className="mr-2"
                  />
                  <span className="text-sm">{topic}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">3. Meeting Platform</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {platforms.map(platform => (
                <label
                  key={platform.id}
                  className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                    formData.meetingPlatform === platform.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-primary-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="meetingPlatform"
                    value={platform.id}
                    checked={formData.meetingPlatform === platform.id}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  <span className="mr-2">{platform.icon}</span>
                  <span className="text-sm">{platform.name}</span>
                </label>
              ))}
            </div>
            {formData.meetingPlatform === 'other' && (
              <input
                type="text"
                name="otherPlatform"
                value={formData.otherPlatform}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Please specify your preferred platform"
                required
              />
            )}
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-bold mb-2">4. Select Your Preferred Time Slots</h2>
            <p className="text-sm text-gray-600 mb-4">
              Select up to 3 preferred time slots. We will confirm the best available slot with you.
            </p>
            <div className="space-y-2">
              {availableSlots.map(slot => (
                <label
                  key={slot.id}
                  className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                    formData.selectedSlots.includes(slot.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.selectedSlots.includes(slot.id)}
                    onChange={() => handleSlotToggle(slot.id)}
                    disabled={!formData.selectedSlots.includes(slot.id) && formData.selectedSlots.length >= 3}
                    className="mr-4"
                  />
                  <div className="flex-grow">
                    <div className="font-medium">{slot.date}</div>
                    <div className="text-sm text-gray-600">{slot.time}</div>
                  </div>
                  {formData.selectedSlots.includes(slot.id) && (
                    <span className="text-green-600 font-medium">Selected</span>
                  )}
                </label>
              ))}
            </div>
            {formData.selectedSlots.length > 0 && (
              <p className="mt-4 text-sm text-gray-600">
                Selected slots: {formData.selectedSlots.length}/3
              </p>
            )}
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">5. Additional Information (Optional)</h2>
            <textarea
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Any specific questions or information you'd like to share..."
            />
          </div>

          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-bold text-blue-900 mb-2">Payment Information</h3>
            <p className="text-sm text-blue-800 mb-2">
              Total: <span className="font-bold text-xl">$19.90</span> for a 30-minute consultation
            </p>
            <p className="text-xs text-blue-700">
              After submitting your booking request, you will be redirected to secure payment processing.
              Once payment is confirmed, we will review your preferred time slots and send you a confirmation
              with the finalized meeting time and platform details.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary"
            >
              {loading ? 'Submitting...' : 'Submit Booking Request'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default MeetingBooking;
